// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import type { HttpEvent, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { HttpErrorResponse, HttpHeaders, HttpResponse } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { catchError, filter, map, switchMap, take, takeUntil, timeout } from 'rxjs/operators';
import type { Observable } from 'rxjs';
import { from, of, throwError, timer, TimeoutError } from 'rxjs';
import { toObservable } from '@angular/core/rxjs-interop';

import { inject } from '@angular/core';
import { DataStore } from '@core/store/data.store';
import { NetworkService } from '@core/services/network.service';
import { LoggerService } from '@core/services/logger.service';
import type { ApiError } from 'src/app/api/pwi/models';
import { AuthVault, isNativeTokenCustody } from '@core/auth/auth-vault';
import { Auth2Service } from '@core/auth/auth.service';

const MAX_429_RETRIES = 2;

// Non-GET requests that are semantically READS (they POST a large query body) or
// otherwise mutate nothing, so they must keep working while impersonating. Every
// OTHER non-GET request is blocked in read-only mode (see isWriteBlockedWhileImpersonating).
// Matched against the ORIGINAL request URL (`/api/pwi/...`, before rewrite).
//   - flights/search          list query
//   - airports/batch          airport lookup (briefing runways / alternates)
//   - .../parameters          replay curve-data fetch (distinct from ope-params, which IS a write)
//   - reports/{handle}/run    report compute — produces a view, persists nothing
const READONLY_SAFE_NON_GET: RegExp[] = [
  /\/flights\/search$/,
  /\/airports\/batch$/,
  /\/parameters$/,
  /\/reports\/[^/]+\/run$/,
];

/**
 * True when `req` would mutate backend state on the impersonated pilot's behalf
 * and must be blocked in read-only mode. GET is always allowed; every other verb
 * is blocked unless it matches the reads-over-POST allowlist above. Fail-safe by
 * design: a future unknown write is blocked by default, a future safe read must
 * be added to the allowlist.
 */
function isWriteBlockedWhileImpersonating(req: HttpRequest<unknown>): boolean {
  if (req.method === 'GET') return false;
  return !READONLY_SAFE_NON_GET.some((re) => re.test(req.url));
}

// Per-attempt request timeout. Generous — data calls only; nothing streams over
// HTTP here (SignalR is separate and not routed through this interceptor). A
// dead connection that `navigator.onLine` can't detect (online-but-unreachable)
// falls back to this so requests don't hang indefinitely.
const REQUEST_TIMEOUT_MS = 30_000;

/**
 * PwiApi error response → HttpErrorResponse + attached `apiError`.
 * Callers in `catchError` can read `(err as HttpErrorResponse & { apiError?: ApiError }).apiError`
 * for the structured PwiApi error (`message`, `detail`, `correlationId`).
 */
function toApiErrorResponse(url: string, status: number, apiError: ApiError, logger: LoggerService): HttpErrorResponse {
  logger.error('API', apiError.message, { detail: apiError.detail, correlationId: apiError.correlationId });
  const err = new HttpErrorResponse({
    status: status || apiError.statusCode || 500,
    statusText: apiError.message ?? 'API error',
    url,
    error: apiError,
  });
  (err as HttpErrorResponse & { apiError: ApiError }).apiError = apiError;
  return err;
}

function isApiErrorShape(body: unknown): body is { error: ApiError } {
  return (
    typeof body === 'object' &&
    body !== null &&
    'error' in body &&
    typeof (body as { error: unknown }).error === 'object' &&
    (body as { error: unknown }).error !== null
  );
}

// ── iOS native transport ─────────────────────────────────────────────────────
// On iOS the access token lives ONLY in the Keychain, so /api calls cannot go out
// through the normal Angular backend — there is no token in JS to attach. Instead
// the fully-shaped request (URL rewritten, impersonation/version headers set, but
// NO Authorization header) is handed to the native vault, which attaches the
// Bearer natively and performs the request. The token never enters JS.

/**
 * JSON is the app's only live /api content type (verified: the generated
 * blob/multipart paths are unreachable from feature code). A string transport
 * would corrupt binary responses/bodies, so those fail LOUD here rather than
 * silently — extend @ergoss/auth-vault (base64 both ways) before wiring any such
 * call on iOS.
 */
function assertNativeTransportSupports(req: HttpRequest<unknown>): void {
  if (req.responseType === 'blob' || req.responseType === 'arraybuffer') {
    throw new Error(
      `native token custody: responseType '${req.responseType}' unsupported (${req.url}) — extend @ergoss/auth-vault for binary`,
    );
  }
  const body = req.body;
  if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
    throw new Error(
      `native token custody: binary/multipart request body unsupported (${req.url}) — extend @ergoss/auth-vault`,
    );
  }
}

/** Mirror HttpXhrBackend's default body serialization: strings pass through,
 *  objects become JSON (and default the Content-Type). */
function serializeBodyForNative(body: unknown, headers: Record<string, string>): string | undefined {
  if (body === null || body === undefined) return undefined;
  if (typeof body === 'string') return body;
  if (!Object.keys(headers).some((k) => k.toLowerCase() === 'content-type')) {
    headers['Content-Type'] = 'application/json';
  }
  return JSON.stringify(body);
}

/** Parse the native response body per the request's responseType (json is the
 *  Angular default). A text body is returned as-is. */
function parseNativeBody(body: string, responseType: HttpRequest<unknown>['responseType']): unknown {
  if (responseType !== 'json') return body;
  if (!body) return null;
  try {
    return JSON.parse(body);
  } catch {
    return body;
  }
}

/** {status, headers, body} from the vault → an Angular HttpEvent. 2xx becomes an
 *  HttpResponse; any other status is thrown as an HttpErrorResponse so the shared
 *  429-retry / ApiError-unwrap logic downstream handles it exactly as on web. */
function nativeResultToHttpEvent(
  req: HttpRequest<unknown>,
  res: { status: number; headers: Record<string, string>; body: string },
): HttpEvent<unknown> {
  const parsed = parseNativeBody(res.body, req.responseType);
  const headers = new HttpHeaders(res.headers);
  if (res.status >= 200 && res.status < 300) {
    return new HttpResponse({ body: parsed, status: res.status, statusText: 'OK', url: req.url, headers });
  }
  throw new HttpErrorResponse({ error: parsed, status: res.status, statusText: 'Error', url: req.url, headers });
}

/** iOS transport: send the fully-authed request through the native vault. */
function sendViaVault(req: HttpRequest<unknown>): Observable<HttpEvent<unknown>> {
  assertNativeTransportSupports(req);
  const headers: Record<string, string> = {};
  req.headers.keys().forEach((key) => {
    const value = req.headers.get(key);
    if (value !== null) headers[key] = value;
  });
  const body = serializeBodyForNative(req.body, headers);
  // urlWithParams, NOT url: Angular keeps query params in req.params and only
  // appends them via urlWithParams (the normal backend does this for the web
  // path). Using req.url here would drop every ?query= — e.g. the report
  // catalogue's ?mode=, silently returning an empty list on iOS.
  return from(AuthVault.authorizedRequest({ url: req.urlWithParams, method: req.method, headers, body })).pipe(
    map((res) => nativeResultToHttpEvent(req, res)),
    catchError((err: unknown) => {
      // An HttpErrorResponse here is our own non-2xx throw above — let it flow to
      // the shared error handling untouched. Anything else is a native reject
      // (transport failure, or SESSION_LOST when the refresh token is dead):
      // a dead refresh token → 401 so the app surfaces re-login (the ONE case iOS
      // must re-auth); other rejects → 0 (transport failure).
      if (err instanceof HttpErrorResponse) return throwError(() => err);
      const message = (err as { message?: string }).message ?? String(err);
      const status = message.includes('SESSION_LOST') ? 401 : 0;
      return throwError(() => new HttpErrorResponse({ status, statusText: message, url: req.url }));
    }),
  );
}

export const httpHandlerInterceptor: HttpInterceptorFn = (req, next) => {
  const ds = inject(DataStore);
  const network = inject(NetworkService);
  const logger = inject(LoggerService);
  const auth = inject(Auth2Service);

  // Non-API calls bypass auth + retry/unwrap logic entirely.
  if (!req.url.startsWith('/api')) {
    return next(req);
  }

  // Read-only impersonation net: while impersonating a pilot the session is
  // strictly read-only, so reject any state-mutating write here. This is the
  // defense-in-depth backstop behind the UI guards (hidden/disabled write
  // controls) and the store-method early-returns — even a control we missed
  // cannot write against the impersonated pilot's data. Reads (GET) and the
  // reads-over-POST allowlist pass through untouched. A blocked write is logged
  // so a missed UI guard surfaces during development rather than silently 403ing.
  if (ds.isImpersonating() && isWriteBlockedWhileImpersonating(req)) {
    logger.warn('IMPERSONATION', `read-only: blocked ${req.method} ${req.url}`);
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 403,
          statusText: 'Read-only (impersonation)',
          url: req.url,
        }),
    );
  }

  // Device-offline: fail fast with a clear error instead of waiting out the
  // request timeout — the device link is reliable when FALSE (see
  // NetworkService). Deliberately gated on the DEVICE link, not `isOnline`:
  // while only the server is marked unreachable, requests must still go out —
  // they are the recovery probes that clear the evidence. status 0 mirrors the
  // browser's own network-failure status so callers treat it like any
  // connectivity error.
  if (!network.deviceConnected()) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 0,
          statusText: 'Offline',
          url: req.url,
        }),
    );
  }

  // Session was wiped (idle timeout or pre-expiry guard in Auth2Service):
  // fail fast with 401 instead of hanging on a token signal that won't
  // come back without a re-login.
  if (ds.tokenExpired() || ds.userIdle() || auth.sessionInvalidated()) {
    return throwError(
      () =>
        new HttpErrorResponse({
          status: 401,
          statusText: 'Session expired',
          url: req.url,
        }),
    );
  }

  // Gate on the token signal becoming non-null. Without this, calls fired
  // during the OAuth code-exchange XHR (or before `parseJwtAndEmitClaims`
  // patches DataStore.token) go out as `Authorization: Bearer null` and
  // are rejected by the gateway.
  //
  // Fast path (synchronous) when the token is already populated — avoids
  // the microtask-scheduled effect inside `toObservable`, which can race
  // against rxMethod's switchMap cancellation when an init-time call is
  // followed shortly by another (e.g. setupAccessData re-running because
  // ergsClaims gets a fresh reference).
  const tokenNow = ds.token();
  const token$: Observable<string> = tokenNow
    ? of(tokenNow)
    : toObservable(ds.token).pipe(
        filter((t): t is string => !!t),
        take(1),
      );

  const authedReq$: Observable<HttpRequest<unknown>> = token$.pipe(
    map((token) => {
      const isPwi = req.url.startsWith('/api/pwi/');
      const customer = ds.impDomain() || ds.claims()?.ergossPwiDomain || '';
      const headers: Record<string, string> = {};
      // iOS attaches the Bearer NATIVELY inside the vault (the token never enters
      // JS); every other platform rides it on the request header as usual. `token`
      // here is the readiness sentinel on iOS — deliberately not sent.
      if (!isNativeTokenCustody()) {
        headers['Authorization'] = 'Bearer ' + token;
      }

      let url: string;
      if (isPwi) {
        // PWI routes → PwiApi (new backend)
        url = environment.apiUrl + req.url;
        if (customer) {
          headers['X-Auth-Customer'] = customer;
        }
      } else {
        // Legacy routes → SARA3API. All remaining legacy calls run on v4 —
        // SARA3API's DefaultApiVersion is still 3 server-side, but IPWI is a
        // v4 client and several endpoints (e.g. GetApproaches) are pinned to v4.
        const baseUrl = environment.legacyApiUrl;
        url = baseUrl + req.url.replace('_', customer);
        headers['x-api-Version'] = '4';
      }

      const code = ds.impCode();
      if (code) {
        headers['erg-code-impersonation'] = code;
      }
      return req.clone({ url, setHeaders: headers });
    }),
  );

  return authedReq$.pipe(
    switchMap((authedReq) => {
      const isPwi = authedReq.url.includes('/api/pwi/');

      // iOS routes through the native vault (token attached natively); web + the
      // rest use the normal Angular backend. Everything downstream — timeout,
      // 429-retry, ApiError unwrap — is identical for both.
      const transport = (r: HttpRequest<unknown>): Observable<HttpEvent<unknown>> =>
        isNativeTokenCustody() ? sendViaVault(r) : next(r);

      const sendRequest = (attempt: number): Observable<HttpEvent<unknown>> =>
        (auth.sessionInvalidated()
          ? throwError(() => new HttpErrorResponse({ status: 401 }))
          : transport(authedReq)
        ).pipe(
          timeout(REQUEST_TIMEOUT_MS),
          map((event) => {
            if (event instanceof HttpResponse) {
              // Reachability evidence (NetworkService): the server answered.
              network.reportServerReachable(true);
              // Soft error: 2xx response carrying `{ error: ApiError }` instead of data.
              // Rare in practice (PwiApi typically pairs errors with non-2xx) but the
              // model allows it, so honour it.
              if (isPwi && isApiErrorShape(event.body)) {
                throw toApiErrorResponse(authedReq.url, event.status, event.body.error, logger);
              }
            }
            return event;
          }),
          catchError((error: unknown) => {
            // Request exceeded REQUEST_TIMEOUT_MS → normalise to a 408 so callers
            // handle it like any other HttpErrorResponse rather than a raw rxjs error.
            if (error instanceof TimeoutError) {
              network.reportServerReachable(false);
              logger.error('HTTP', `timeout after ${REQUEST_TIMEOUT_MS}ms`, authedReq.url);
              return throwError(
                () => new HttpErrorResponse({ status: 408, statusText: 'Request timed out', url: authedReq.url }),
              );
            }

            const httpError = error as HttpErrorResponse;
            // Reachability evidence: status 0 is a transport-level failure (the
            // request went out, nothing answered); any status > 0 came FROM the
            // server, which is therefore reachable even when erroring.
            network.reportServerReachable(httpError.status > 0);
            if (httpError.status === 429 && attempt < MAX_429_RETRIES) {
              const retryAfter = parseInt(httpError.headers.get('Retry-After') || '5', 10);
              const delayMs = retryAfter * 1000;
              logger.warn(
                'HTTP',
                `429 Too Many Requests — retrying in ${retryAfter}s (attempt ${attempt + 1}/${MAX_429_RETRIES})`,
              );
              return timer(delayMs).pipe(switchMap(() => sendRequest(attempt + 1)));
            }

            // Hard error from PwiApi with an ApiResponse-shaped body → surface the
            // structured ApiError to callers.
            if (isPwi && isApiErrorShape(httpError.error)) {
              return throwError(() =>
                toApiErrorResponse(authedReq.url, httpError.status, httpError.error.error, logger),
              );
            }

            // Anything else (raw 5xx, network failure, non-PwiApi errors): rethrow
            // unchanged. Callers get the standard HttpErrorResponse.
            logger.error('HTTP', `${httpError.status} ${authedReq.url}`, httpError.message);
            return throwError(() => httpError);
          }),
        );

      return sendRequest(0);
    }),
    takeUntil(auth.sessionEnded$),
  );
};
